import * as React from 'react';
import { TopNav } from './nav.jsx';
import { Icon } from './icons.jsx';
import { HomeScreen } from './screens/home.jsx';
import { ResultsScreen } from './screens/results.jsx';
import { ProfileScreen } from './screens/profile.jsx';
import { DashboardScreen } from './screens/dashboard.jsx';
import { AssistantScreen } from './screens/assistant.jsx';
import { BookingModal } from './screens/booking.jsx';
import { WaitlistModal } from './screens/waitlist.jsx';
import { CompassConceptScreen } from './screens/compass-concept.jsx';
import { TextDemoScreen } from './screens/text-demo.jsx';
import { OnboardingScreen } from './screens/onboarding.jsx';
import { FamilyRequestsScreen } from './screens/family-requests.jsx';
import { PartnerDashboardScreen } from './screens/partner-dashboard.jsx';
import { ComparisonScreen } from './screens/compare.jsx';
import { ResourcesScreen } from './screens/resources.jsx';
import { FeedbackPanel } from './feedback.jsx';
import { ProviderAccessScreen } from './screens/provider-access.jsx';
import { ClaimReviewScreen } from './screens/claim-review.jsx';
import { ProviderWorkspaceScreen } from './screens/provider-workspace.jsx';
import {
  LiveCareProfileScreen,
  LiveCareResultsScreen,
} from './screens/live-care.jsx';

// Find My Childcare - app root + router.

const { useState, useEffect, useMemo } = React;

const SCREEN_LABEL = {
  home:      '01 Homepage',
  results:   '02 Search Results',
  profile:   '03 Provider Profile',
  dashboard: '04 Provider Dashboard',
  assistant: '05 Provider Assistant',
  compass: '06 COMPASS Extension Concept',
  textdemo: '07 SMS Demo',
  onboarding: '08 Provider Onboarding',
  requests: '09 Family Requests',
  partner: '10 Partner Dashboard',
  compare: '11 Family Comparison',
  resources: '12 Child Care Works Resources',
  'provider-access': '13 Provider Access',
  'claim-review': '14 Claim Review',
  'provider-workspace': '15 Provider Workspace',
  'pilot-results': '16 Live Pilot Search',
  'pilot-profile': '17 Live Pilot Provider',
};
const VALID_ROUTES = ['home', 'results', 'profile', 'dashboard', 'assistant', 'compass', 'textdemo', 'onboarding', 'requests', 'partner', 'compare', 'resources', 'provider-access', 'claim-review', 'provider-workspace', 'pilot-results', 'pilot-profile'];
const VALID_AUDIENCES = ['parents', 'providers', 'partners'];

const readInitialParam = (key, fallback) => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(key) || fallback;
  } catch {
    return fallback;
  }
};

const readInitialAudience = () => {
  const fromUrl = readInitialParam('audience', '');
  if (VALID_AUDIENCES.includes(fromUrl)) return fromUrl;
  try {
    const stored = window.localStorage.getItem('fmc_audience');
    if (VALID_AUDIENCES.includes(stored)) return stored;
  } catch {}
  return 'parents';
};

const readInitialRoute = () => {
  const fromUrl = readInitialParam('route', '');
  if (VALID_ROUTES.includes(fromUrl)) return fromUrl;
  const audienceFromUrl = readInitialParam('audience', '');
  if (VALID_AUDIENCES.includes(audienceFromUrl)) return 'home';
  try {
    const stored = window.localStorage.getItem('fmc_route');
    if (VALID_ROUTES.includes(stored)) return stored;
  } catch {}
  return 'home';
};

const persistLocationState = ({ route, audience, providerId, comparisonIds = [] }) => {
  try {
    window.localStorage.setItem('fmc_audience', audience);
    window.localStorage.setItem('fmc_route', route);
    const url = new URL(window.location.href);
    url.searchParams.set('audience', audience);
    url.searchParams.set('route', route);
    if (providerId) url.searchParams.set('provider', providerId);
    else url.searchParams.delete('provider');
    if (route === 'compare' && comparisonIds.length) {
      url.searchParams.set('compare', comparisonIds.join(','));
    } else {
      url.searchParams.delete('compare');
    }
    window.history.replaceState({}, '', url);
  } catch {}
};

export const App = () => {
  const [route, setRoute] = useState(readInitialRoute);
  const [audience, setAudience] = useState(readInitialAudience);
  const [query, setQuery] = useState({
    zip: '19130',
    childrenAges: ['Young Toddler'],
    schedule: 'Full time',
  });
  const [providerId, setProviderId] = useState(() => readInitialParam('provider', null));
  const [comparisonIds, setComparisonIds] = useState(() => {
    const fromUrl = readInitialParam('compare', '');
    return fromUrl ? fromUrl.split(',').filter(Boolean).slice(0, 3) : ['sunnyside', 'cedar-house'];
  });
  const [showPrototypeTools] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).has('demo');
    } catch {
      return false;
    }
  });
  const [useLiveCareSearch] = useState(
    () => window.FMC_CONFIG?.useLiveCareSearch === true,
  );

  // Booking modal state: global so it can fire from any screen.
  const [booking, setBooking] = useState({ open: false, providerId: null, tourType: 'in-person' });
  const [waitlist, setWaitlist] = useState({ open: false, providerId: null, age: null });

  const go = (next, payload = {}) => {
    const nextAudience = VALID_AUDIENCES.includes(payload.audience) ? payload.audience : audience;
    const nextProviderId = payload.providerId || providerId;
    if (payload.audience) setAudience(nextAudience);
    if (payload.zip !== undefined || payload.childrenAges || payload.schedule) {
      setQuery(prev => ({
        zip:          payload.zip !== undefined ? payload.zip : prev.zip,
        childrenAges: payload.childrenAges || prev.childrenAges,
        schedule:     payload.schedule || prev.schedule,
      }));
    }
    if (payload.providerId) setProviderId(payload.providerId);
    const nextComparisonIds = payload.providerIds || comparisonIds;
    if (payload.providerIds) setComparisonIds(payload.providerIds);
    setRoute(next);
    persistLocationState({
      route: next,
      audience: nextAudience,
      providerId: next === 'profile' ? nextProviderId : null,
      comparisonIds: nextComparisonIds,
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const selectAudience = (nextAudience) => {
    setAudience(nextAudience);
    persistLocationState({ route: 'home', audience: nextAudience, providerId: null });
    if (route !== 'home') setRoute('home');
    window.scrollTo({ top: 0, behavior: route === 'home' ? 'smooth' : 'instant' });
  };

  const openBooking = (id, type = 'in-person') => {
    setBooking({ open: true, providerId: id, tourType: type });
  };
  const closeBooking = () => setBooking(b => ({ ...b, open: false }));
  const openWaitlist = (id, age = null) => setWaitlist({ open: true, providerId: id, age });
  const closeWaitlist = () => setWaitlist(current => ({ ...current, open: false }));

  // Family-facing nav appears on home/results/profile; dashboard has its own sidebar.
  const showTopNav =
    route !== 'dashboard' &&
    route !== 'assistant' &&
    route !== 'onboarding' &&
    route !== 'provider-workspace';
  const showFeedback = ![
    'provider-workspace',
    'claim-review',
    'pilot-results',
    'pilot-profile',
  ].includes(route);

  return (
    <div data-screen-label={SCREEN_LABEL[route]} style={{ minHeight: '100vh' }}>
      {showTopNav && <TopNav go={go} route={route} transparent={route === 'home'}/>}

      <main id="main" tabIndex="-1">
        {route === 'home'      && <HomeScreen     go={go} audience={audience} onAudienceChange={selectAudience} familySearchRoute={useLiveCareSearch ? 'pilot-results' : 'results'}/>}
        {route === 'results'   && <ResultsScreen  go={go} query={query} onBook={openBooking} onWaitlist={openWaitlist}/>}
        {route === 'profile'   && <ProfileScreen  go={go} providerId={providerId || 'sunnyside'} onBook={openBooking} onWaitlist={openWaitlist}/>}
        {route === 'dashboard' && <DashboardScreen go={go}/>}
        {route === 'assistant' && <AssistantScreen go={go}/>}
        {route === 'compass' && (
          <CompassConceptScreen go={go} onBook={openBooking} onWaitlist={openWaitlist}/>
        )}
        {route === 'textdemo' && <TextDemoScreen go={go}/>}
        {route === 'onboarding' && <OnboardingScreen go={go}/>}
        {route === 'requests' && <FamilyRequestsScreen go={go}/>}
        {route === 'partner' && <PartnerDashboardScreen go={go}/>}
        {route === 'compare' && (
          <ComparisonScreen
            go={go}
            providerIds={comparisonIds}
            searchedAges={query.childrenAges}
            onBook={openBooking}
            onWaitlist={openWaitlist}
          />
        )}
        {route === 'resources' && <ResourcesScreen go={go}/>}
        {route === 'provider-access' && <ProviderAccessScreen go={go}/>}
        {route === 'claim-review' && <ClaimReviewScreen go={go}/>}
        {route === 'provider-workspace' && (
          <ProviderWorkspaceScreen go={go} providerId={providerId}/>
        )}
        {route === 'pilot-results' && (
          <LiveCareResultsScreen go={go} query={query}/>
        )}
        {route === 'pilot-profile' && (
          <LiveCareProfileScreen
            go={go}
            providerId={providerId || '100128144-0059'}
          />
        )}
      </main>

      <BookingModal
        open={booking.open}
        providerId={booking.providerId}
        initialTourType={booking.tourType}
        onClose={closeBooking}
      />
      <WaitlistModal
        open={waitlist.open}
        providerId={waitlist.providerId}
        initialAge={waitlist.age}
        onClose={closeWaitlist}
      />
      {showFeedback && <FeedbackPanel route={route}/>}

      {/* Floating route-jumper: visible only when ?demo is in URL or always for prototype clarity.
          Keeps screen-jumping fast without breaking the family flow. */}
      {showPrototypeTools && route !== 'assistant' && <RouteJumper route={route} go={go}/>}
    </div>
  );
};

const RouteJumper = ({ route, go }) => {
  const [open, setOpen] = useState(false);
  const routes = [
    { id: 'home',      label: 'Homepage',        icon: 'home' },
    { id: 'results',   label: 'Search Results',  icon: 'search' },
    { id: 'profile',   label: 'Provider Profile',icon: 'image' },
    { id: 'dashboard', label: 'Provider Dashboard', icon: 'grid' },
    { id: 'assistant', label: 'Provider Assistant', icon: 'spark' },
    { id: 'compass', label: 'COMPASS Concept', icon: 'shield' },
    { id: 'textdemo', label: 'SMS Demo', icon: 'phone' },
    { id: 'onboarding', label: 'Provider Onboarding', icon: 'plus' },
    { id: 'requests', label: 'Family Requests', icon: 'calendar-check' },
    { id: 'partner', label: 'Partner Dashboard', icon: 'grid' },
    { id: 'compare', label: 'Family Comparison', icon: 'sliders' },
    { id: 'resources', label: 'Child Care Works', icon: 'shield' },
    { id: 'provider-access', label: 'Provider Access', icon: 'user' },
    { id: 'claim-review', label: 'Claim Review', icon: 'shield' },
    { id: 'provider-workspace', label: 'Provider Workspace', icon: 'calendar-check' },
    { id: 'pilot-results', label: 'Live Pilot Search', icon: 'search' },
  ];

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 60,
    }}>
      {open && (
        <div id="prototype-screen-menu" style={{
          position: 'absolute', bottom: 56, right: 0,
          background: 'var(--surface)', border: '1px solid var(--line-2)',
          borderRadius: 16, boxShadow: 'var(--shadow-lg)',
          padding: 10, minWidth: 240,
          animation: 'pop .18s ease',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--ink-3)',
            letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '6px 10px 10px',
          }}>
            Prototype screens
          </div>
          {routes.map(r => (
            <button
              key={r.id}
              onClick={() => { go(r.id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '10px 12px',
                background: route === r.id ? 'var(--brand-tint)' : 'transparent',
                color: route === r.id ? 'var(--brand-ink)' : 'var(--ink-2)',
                border: 'none', borderRadius: 10, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, fontWeight: route === r.id ? 700 : 500,
                textAlign: 'left',
              }}
            >
              <Icon name={r.icon} size={15} style={{ color: route === r.id ? 'var(--brand)' : 'var(--ink-3)' }}/>
              <span style={{ flex: 1 }}>{r.label}</span>
              {route === r.id && <Icon name="check" size={14} style={{ color: 'var(--brand)' }}/>}
            </button>
          ))}
          <div style={{
            borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 8,
            fontSize: 11, color: 'var(--ink-4)', padding: '8px 12px 4px',
          }}>
            Esc closes modals
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="prototype-screen-menu"
        style={{
          background: 'var(--ink)', color: '#fff',
          border: 'none', borderRadius: 999,
          padding: '11px 16px 11px 14px',
          fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <span style={{
          background: 'rgba(255,255,255,0.16)', color: '#fff',
          borderRadius: 999, padding: '3px 6px', fontSize: 10,
          textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 900,
        }}>Prototype</span>
        <Icon name="grid" size={14}/>
        Screens
        <Icon name={open ? 'chev-down' : 'chev-right'} size={12} style={{ transform: open ? 'rotate(180deg)' : 'rotate(-90deg)', transition: 'transform .15s ease' }}/>
      </button>
    </div>
  );
};
